import { useTranslation } from 'react-i18next';

import { PILLAR_TILE, PLACEHOLDER_BONE_COLOR } from './objectiveStyles';

import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';

/**
 * As many placeholders as the row shows tiles at its widest: `--obj-pillar-columns` (`app.css`)
 * reaches four from 1024px, so four tiles fill the row exactly there, and a narrower view clips
 * the extras the same way the loaded row scrolls them out of sight.
 */
const PLACEHOLDER_TILE_COUNT = 4;

/** A loaded tile runs its description to four lines; three read as a paragraph without filling it. */
const PLACEHOLDER_DESCRIPTION_WIDTHS = ['w-full', 'w-11/12', 'w-2/3'];

/** The two figures every tile carries: its participants and its progression. */
const PLACEHOLDER_STAT_ROWS = 2;

/**
 * One tile of the row, drawn as the blocks the loaded tile puts in its place -- its icon, its
 * count, its title and description, its two figures and its progress bar -- so the row reads as
 * tiles being filled in rather than as empty frames.
 */
function PillarTilePlaceholder() {
    return (
        <div
            className={`${PILLAR_TILE.surface} bg-white`}
            data-testid={'pillar-summary-placeholder'}
        >
            <div className={PILLAR_TILE.header}>
                <Bone
                    color={PLACEHOLDER_BONE_COLOR}
                    className={PILLAR_TILE.iconBox}
                    dataTestId={'pillar-placeholder-icon'}
                />

                <Bone
                    color={PLACEHOLDER_BONE_COLOR}
                    className={'h-6 w-20 rounded-full'}
                />
            </div>

            <div className={'flex min-h-0 flex-1 flex-col gap-3'}>
                <Bone
                    color={PLACEHOLDER_BONE_COLOR}
                    className={'h-5 w-3/4'}
                    dataTestId={'pillar-placeholder-title'}
                />

                {PLACEHOLDER_DESCRIPTION_WIDTHS.map((width) => (
                    <Bone
                        key={width}
                        color={PLACEHOLDER_BONE_COLOR}
                        className={`h-4 ${width}`}
                    />
                ))}
            </div>

            <div className={PILLAR_TILE.statsBlock}>
                {Array.from({ length: PLACEHOLDER_STAT_ROWS }, (_, index) => (
                    <div
                        key={index}
                        className={'flex items-center justify-between'}
                    >
                        <Bone
                            color={PLACEHOLDER_BONE_COLOR}
                            className={'h-5 w-32'}
                        />

                        <Bone
                            color={PLACEHOLDER_BONE_COLOR}
                            className={'h-5 w-10'}
                        />
                    </div>
                ))}
            </div>

            <div className={PILLAR_TILE.progressAnchor}>
                <Bone
                    color={PLACEHOLDER_BONE_COLOR}
                    className={PILLAR_TILE.progressTrack}
                    dataTestId={'pillar-placeholder-progress'}
                />
            </div>
        </div>
    );
}

/**
 * What stands in the pillar row while its totals are being read: the row keeps its place and its
 * height, and each tile is drawn as the blocks it is about to hold. The tiles carry the card's own
 * geometry classes, so the placeholder and what replaces it are the same size by construction.
 */
export default function PillarSummariesSkeleton() {
    const { t } = useTranslation();

    return (
        <div
            role={'status'}
            aria-busy
            aria-label={t('objectives.pillars.loading')}
            data-testid={'pillar-summaries-skeleton'}
        >
            <Skeleton className={'obj-pillar-grid flex overflow-hidden'}>
                {Array.from({ length: PLACEHOLDER_TILE_COUNT }, (_, index) => (
                    <div
                        key={index}
                        className={'obj-pillar-tile flex-shrink-0'}
                    >
                        <PillarTilePlaceholder />
                    </div>
                ))}
            </Skeleton>
        </div>
    );
}
